import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { COLORS, FlexView } from '@cuemath/comps';
import { Text, Button as WCButton } from '@cuemath/web-comps';

import { postStudentTransferStatus } from 'actions/student-transfer/single-student';
import {
  convertSlotsDataToText,
  getDateOfRequest,
  getSingleStudentTransferPostData,
  singleStudentTransferButtonDisable,
} from 'utils/student-transfer';

import SlotCreation from './slot-creation';
import AssignTeacher from './assign-teacher';

const Root = styled(FlexView)`
  gap: 16px;
  padding: 8px 0px;
`;

const Button = styled(WCButton)`
  width: 100%;
  height: 48px;
`;

export const slotsInitState = [
  {
    day: '',
    time: '',
    id: `student-transfer-slots-${1}`,
  },
];

const StudentTransferSlotPending = ({
  confirmSlots: confirmSlotsProps,
  studentName,
  teacherName,
  grade,
  region,
  classBalance,
  tenureEndDate,
  regularSlots,
  reasonForTransfer,
  studentId,
  assigningSingleStudent,
  preferredTeacherId,
}) => {
  const [slotsList, setSlotsList] = useState(slotsInitState);
  const [confirmSlots, setConfirmSlots] = useState(false);
  const [didFindTeacher, setDidFindTeacher] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [newPreferredTeacherId, setNewPreferredTeacherId] = useState('');

  const onStudentTransferCompletion = (errorMessage, data) => {
    if (errorMessage) {
      setErrorMessage(errorMessage);
    } else {
      setDidFindTeacher(true);

      if (typeof assigningSingleStudent === 'function') {
        assigningSingleStudent();
      }
    }
  };

  const { post: initiateStudentTransfer, isProcessing } = postStudentTransferStatus({
    onComplete: onStudentTransferCompletion,
  });

  const assignTeacher = confirmSlots || confirmSlotsProps;

  const addMoreSlots = () => {
    setSlotsList(prev => [
      ...prev,
      {
        day: '',
        time: '',
        id: `student-transfer-slots-${prev.length + 1}`,
      },
    ]);
  };

  const updateSlotsList = useCallback(updateSlotData => {
    setSlotsList(prev =>
      prev.map(slot => {
        if (slot.id === updateSlotData.id) {
          return updateSlotData;
        }
        return slot;
      }),
    );
  }, []);

  const findAndAssingMathTeacher = () => {
    const finalData = getSingleStudentTransferPostData({
      transferReason: reasonForTransfer,
      slotsList,
      preferredTeacherData: {
        preferredTeacherId,
      },
    });

    initiateStudentTransfer(finalData, studentId);
  };

  const handleSlotPendingConfirmation = () => {
    setErrorMessage('');

    if (assignTeacher) {
      findAndAssingMathTeacher();
    } else {
      setConfirmSlots(prev => !prev);
    }
  };

  const disableBtn =
    didFindTeacher ||
    (!confirmSlotsProps &&
      singleStudentTransferButtonDisable({
        transferReason: 'Class slot issues',
        slotsList,
      }));
  const btnLabel = assignTeacher ? `Find and assign teacher` : `Confirm regular slots`;

  const regularSlotToText = convertSlotsDataToText(slotsList);
  const message = typeof errorMessage !== 'string' ? errorMessage?.reason : errorMessage;

  const slotInfoPreview = {
    student_name: studentName,
    teacher_name: teacherName,
    grade,
    region,
    class_balance: classBalance,
    tenure_end_date: getDateOfRequest(tenureEndDate),
    regular_slots: regularSlots || regularSlotToText,
    preferred_teacher_id: preferredTeacherId,
  };

  return (
    <Root>
      {assignTeacher ? (
        <AssignTeacher data={slotInfoPreview} />
      ) : (
        <SlotCreation
          addSlots={addMoreSlots}
          data={slotInfoPreview}
          newPreferredTeacherId={newPreferredTeacherId}
          reasonForTransfer={reasonForTransfer}
          setNewPreferredTeacherId={setNewPreferredTeacherId}
          setSlotsList={setSlotsList}
          slotsList={slotsList}
          updateSlotsList={updateSlotsList}
        />
      )}

      <Button
        label={btnLabel}
        isDisabled={disableBtn}
        isBusy={isProcessing}
        onClick={handleSlotPendingConfirmation}
      />
      {message && (
        <Text color={COLORS.RED_SHADE} variant="eyebrow2">
          {message}
        </Text>
      )}
    </Root>
  );
};

export default StudentTransferSlotPending;
